import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { fetchClientCosts, fetchClientOverview } from "./api";

/**
 * One shared cache of per-client reads (usage overviews and real costs) for
 * the whole Pilots page, so a client opened in one tab is not fetched again in
 * another.
 */
const ClientDataContext = createContext(null);

const OVERVIEW_ERROR = "Could not load this client's usage.";
const COSTS_ERROR = "Could not load this client's costs.";
const EMPTY_ENTRY = { loading: false, error: null, data: null };

export function ClientOverviewProvider({ env, children }) {
  const [entries, setEntries] = useState({});
  const requested = useRef(new Set());

  useEffect(() => {
    requested.current = new Set();
    setEntries({});
  }, [env]);

  const load = useCallback(
    async (key, request, fallbackError, { force = false } = {}) => {
      if (!force && requested.current.has(key)) {
        return;
      }
      requested.current.add(key);
      setEntries((current) => ({
        ...current,
        [key]: { loading: true, error: null, data: current[key]?.data },
      }));
      try {
        const json = await request(env);
        if (!json?.success) {
          throw new Error(json?.reason || fallbackError);
        }
        setEntries((current) => ({
          ...current,
          [key]: { loading: false, error: null, data: json },
        }));
      } catch (err) {
        setEntries((current) => ({
          ...current,
          [key]: { loading: false, error: err.message || fallbackError, data: null },
        }));
      }
    },
    [env],
  );

  return (
    <ClientDataContext.Provider value={{ entries, load }}>
      {children}
    </ClientDataContext.Provider>
  );
}

const costsKey = (organizationId, days) => `costs:${organizationId}:${days}`;
const costsRequest = (organizationId, days) => (env) =>
  fetchClientCosts(organizationId, env, { days });

/**
 * The usage overview of one client, loaded the first time a component asks.
 *
 * @param {string|null} organizationId
 * @param {{ includeHubSpot?: boolean }} [options]
 */
export function useClientOverview(organizationId, { includeHubSpot = false } = {}) {
  const { entries, load } = useContext(ClientDataContext);
  const key = organizationId ? `overview:${organizationId}` : null;
  const request = useCallback(
    (env) => fetchClientOverview(organizationId, env, { includeHubSpot }),
    [organizationId, includeHubSpot],
  );

  useEffect(() => {
    if (key) {
      load(key, request, OVERVIEW_ERROR);
    }
  }, [key, request, load]);

  return {
    ...(entries[key] || { ...EMPTY_ENTRY, loading: Boolean(key) }),
    retry: () => load(key, request, OVERVIEW_ERROR, { force: true }),
  };
}

/**
 * What Addy really spent on one client over the last `days` days.
 *
 * @param {string|null} organizationId
 * @param {number} days
 */
export function useClientCosts(organizationId, days) {
  const { entries, load } = useContext(ClientDataContext);
  const key = organizationId ? costsKey(organizationId, days) : null;

  useEffect(() => {
    if (key) {
      load(key, costsRequest(organizationId, days), COSTS_ERROR);
    }
  }, [key, organizationId, days, load]);

  return {
    ...(entries[key] || { ...EMPTY_ENTRY, loading: Boolean(key) }),
    retry: () => load(key, costsRequest(organizationId, days), COSTS_ERROR, { force: true }),
  };
}

/**
 * Real costs of several clients at once, each over its own window.
 *
 * @param {Array<{ organizationId: string, days: number }>} requests
 * @returns {Array<{ organizationId: string, loading: boolean, error: string|null, data: object|null }>}
 */
export function useClientCostsList(requests) {
  const { entries, load } = useContext(ClientDataContext);
  const signature = requests.map((entry) => costsKey(entry.organizationId, entry.days)).join("|");

  useEffect(() => {
    if (!signature) {
      return;
    }
    signature.split("|").forEach((key) => {
      const [, organizationId, days] = key.split(":");
      load(key, costsRequest(organizationId, Number(days)), COSTS_ERROR);
    });
  }, [signature, load]);

  return requests.map((entry) => ({
    organizationId: entry.organizationId,
    ...(entries[costsKey(entry.organizationId, entry.days)] || { ...EMPTY_ENTRY, loading: true }),
  }));
}
